import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { Project, ProjectSubtask } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from '../components/Card.tsx';
import { Select } from '../components/Select.tsx';

const DAY_MS = 24 * 60 * 60 * 1000;
const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 40;
const NAME_COL_WIDTH = 220;
const MIN_DAY_WIDTH = 8;

type StatusFilter = 'active' | 'completed' | 'all';
type RangeKey = '60' | '90' | '180' | '365';

export function ProjectsGanttPage(): React.ReactElement {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [subtasksByProject, setSubtasksByProject] = useState<Map<string, ProjectSubtask[]>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [rangeDays, setRangeDays] = useState<RangeKey>('90');

  useEffect(() => {
    let cancelled = false;
    const params = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
    apiFetch<{ projects: Project[] }>(`/api/projects${params}`)
      .then(async (r) => {
        if (cancelled) return;
        setProjects(r.projects);
        setError(null);
        const subMap = new Map<string, ProjectSubtask[]>();
        await Promise.all(
          r.projects.map(async (p) => {
            try {
              const sr = await apiFetch<{ subtasks: ProjectSubtask[] }>(
                `/api/projects/${p.id}/subtasks`,
              );
              subMap.set(p.id, sr.subtasks.filter((s) => s.dueDate));
            } catch {
              /* skip */
            }
          }),
        );
        if (!cancelled) setSubtasksByProject(subMap);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load projects.');
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const rangeStart = useMemo(() => addDays(today, -14), [today]);
  const rangeEnd = useMemo(() => addDays(today, Number(rangeDays)), [today, rangeDays]);
  const totalDays = Math.max(1, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / DAY_MS));
  const dayWidth = Math.max(MIN_DAY_WIDTH, Math.floor(900 / totalDays));
  const chartWidth = totalDays * dayWidth;

  const placed = useMemo(() => {
    if (!projects) return [];
    return projects
      .map((p) => {
        const start = startOfDay(new Date(p.startedAt ?? p.createdAt));
        const cutover = p.cutoverDate ? startOfDay(new Date(p.cutoverDate)) : null;
        const due = p.dueDate ? startOfDay(new Date(p.dueDate)) : null;
        const end = lastNonNull([cutover, due]);
        if (!end) return null;
        const clampedStart = start < rangeStart ? rangeStart : start;
        const clampedEnd = end > rangeEnd ? rangeEnd : end;
        if (clampedEnd < rangeStart || clampedStart > rangeEnd) return null;
        const offsetDays = Math.round((clampedStart.getTime() - rangeStart.getTime()) / DAY_MS);
        const lengthDays = Math.max(
          1,
          Math.round((clampedEnd.getTime() - clampedStart.getTime()) / DAY_MS) + 1,
        );
        const dueOverdue = due !== null && due < today && p.status === 'active';
        const cutoverOverdue = cutover !== null && cutover < today && p.status === 'active';
        return {
          project: p,
          offsetDays,
          lengthDays,
          dueOverdue: dueOverdue || cutoverOverdue,
        };
      })
      .filter(Boolean) as Array<{
      project: Project;
      offsetDays: number;
      lengthDays: number;
      dueOverdue: boolean;
    }>;
  }, [projects, rangeStart, rangeEnd, today]);

  const monthLabels = useMemo(() => {
    const out: Array<{ label: string; xStart: number; xEnd: number }> = [];
    let cursor = new Date(rangeStart);
    while (cursor < rangeEnd) {
      const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const visStart = monthStart < rangeStart ? rangeStart : monthStart;
      const visEnd = monthEnd > rangeEnd ? rangeEnd : monthEnd;
      out.push({
        label: monthStart.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
        xStart: Math.round((visStart.getTime() - rangeStart.getTime()) / DAY_MS) * dayWidth,
        xEnd: Math.round((visEnd.getTime() - rangeStart.getTime()) / DAY_MS) * dayWidth,
      });
      cursor = monthEnd;
    }
    return out;
  }, [rangeStart, rangeEnd, dayWidth]);

  const todayX = Math.round((today.getTime() - rangeStart.getTime()) / DAY_MS) * dayWidth;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Projects · Gantt</h1>
          <p className="mt-1 text-[var(--color-muted)]">
            Bars span project start to whichever is latest of due/cutover. Projects without either are
            not shown.
          </p>
        </div>
        <Link to="/projects" className="text-sm text-[var(--color-t3-blue)] hover:underline">
          ← List view
        </Link>
      </header>

        <Card>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
              <option value="active">Active only</option>
              <option value="completed">Completed only</option>
              <option value="all">All</option>
            </Select>
            <Select label="Range" value={rangeDays} onChange={(e) => setRangeDays(e.target.value as RangeKey)}>
              <option value="60">Today + 60 days</option>
              <option value="90">Today + 90 days</option>
              <option value="180">Today + 180 days</option>
              <option value="365">Today + 1 year</option>
            </Select>
          </div>
        </Card>

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        {projects === null ? (
          <p className="text-[var(--color-muted)]">Loading…</p>
        ) : placed.length === 0 ? (
          <Card>
            <p className="text-center text-[var(--color-muted)]">
              No projects in range with both a start and a due/cutover date.
            </p>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
            <div style={{ width: NAME_COL_WIDTH + chartWidth }}>
              <div className="flex border-b border-[var(--color-border)]" style={{ height: HEADER_HEIGHT }}>
                <div
                  className="flex items-center px-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]"
                  style={{ width: NAME_COL_WIDTH }}
                >
                  Project
                </div>
                <div className="relative" style={{ width: chartWidth, height: HEADER_HEIGHT }}>
                  {monthLabels.map((m, i) => (
                    <div
                      key={i}
                      className="absolute top-0 flex h-full items-center border-l border-[var(--color-border)] px-2 text-xs font-semibold text-[var(--color-muted)]"
                      style={{ left: m.xStart, width: m.xEnd - m.xStart }}
                    >
                      {m.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                {placed.map((row, i) => (
                  <div key={row.project.id} className="flex border-b border-[var(--color-border)]" style={{ height: ROW_HEIGHT }}>
                    <div
                      className="flex items-center truncate px-3 text-sm"
                      style={{ width: NAME_COL_WIDTH }}
                    >
                      <Link
                        to={`/projects/${row.project.id}`}
                        className="truncate text-[var(--color-t3-blue)] hover:underline"
                        title={`${row.project.organizationName} · ${row.project.name}`}
                      >
                        {row.project.name}
                      </Link>
                    </div>
                    <div className="relative" style={{ width: chartWidth, height: ROW_HEIGHT }}>
                      {(() => {
                        const left = row.offsetDays * dayWidth;
                        const width = Math.max(dayWidth - 2, row.lengthDays * dayWidth - 2);
                        const baseColor =
                          row.project.status !== 'active'
                            ? 'rgb(125 125 125 / 0.55)'
                            : row.dueOverdue
                              ? 'rgb(220 38 38 / 0.85)'
                              : row.project.billingMode === 'block_hours'
                                ? 'rgb(0 174 239 / 0.85)'
                                : 'rgb(107 63 160 / 0.85)';
                        return (
                          <div
                            className="absolute top-1 rounded-[var(--radius-button)] text-xs font-semibold leading-7 text-white"
                            style={{
                              left,
                              width,
                              height: ROW_HEIGHT - 8,
                              background: baseColor,
                              paddingLeft: 8,
                              paddingRight: 8,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                            title={[
                              row.project.organizationName,
                              row.project.name,
                              row.project.dueDate ? `due ${row.project.dueDate}` : null,
                              row.project.cutoverDate ? `cutover ${row.project.cutoverDate}` : null,
                            ].filter(Boolean).join(' · ')}
                          >
                            {row.project.organizationName}
                          </div>
                        );
                      })()}
                      {(subtasksByProject.get(row.project.id) ?? []).map((s) => {
                        if (!s.dueDate) return null;
                        const d = new Date(s.dueDate);
                        const offsetDays = Math.round(
                          (d.getTime() - rangeStart.getTime()) / DAY_MS,
                        );
                        if (offsetDays < 0 || offsetDays > totalDays) return null;
                        const x = offsetDays * dayWidth;
                        const isOverdue =
                          s.completedAt === null && d < today && row.project.status === 'active';
                        return (
                          <div
                            key={s.id}
                            className="absolute"
                            style={{
                              left: x - 5,
                              top: ROW_HEIGHT / 2 - 5,
                              width: 10,
                              height: 10,
                              transform: 'rotate(45deg)',
                              background:
                                s.completedAt !== null
                                  ? 'rgb(34 197 94)'
                                  : isOverdue
                                    ? 'rgb(220 38 38)'
                                    : 'rgb(255 255 255)',
                              border: '1.5px solid ' + (
                                s.completedAt !== null
                                  ? 'rgb(34 197 94)'
                                  : isOverdue
                                    ? 'rgb(220 38 38)'
                                    : 'rgb(0 174 239)'
                              ),
                            }}
                            title={`${s.title} — due ${s.dueDate}${
                              s.completedAt ? ' (done)' : isOverdue ? ' (overdue)' : ''
                            }`}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}

                {todayX >= 0 && todayX <= chartWidth && (
                  <div
                    className="absolute top-0 bottom-0"
                    style={{
                      left: NAME_COL_WIDTH + todayX,
                      width: 2,
                      background: 'rgb(220 38 38 / 0.6)',
                    }}
                    title="Today"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-4 text-xs text-[var(--color-muted)]">
          <Legend color="rgb(0 174 239 / 0.85)" label="Active · block hours" />
          <Legend color="rgb(107 63 160 / 0.85)" label="Active · flat fee" />
          <Legend color="rgb(220 38 38 / 0.85)" label="Active · overdue" />
          <Legend color="rgb(125 125 125 / 0.55)" label="Completed / cancelled" />
          <Legend color="rgb(220 38 38 / 0.6)" label="Today" line />
          <Legend color="rgb(0 174 239)" label="Subtask milestone" diamond />
          <Legend color="rgb(34 197 94)" label="Milestone done" diamond />
        </div>
    </div>
  );
}

function Legend({
  color,
  label,
  line,
  diamond,
}: {
  color: string;
  label: string;
  line?: boolean;
  diamond?: boolean;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block"
        style={{
          width: line ? 2 : diamond ? 10 : 14,
          height: line ? 14 : diamond ? 10 : 14,
          background: diamond ? 'white' : color,
          border: diamond ? `1.5px solid ${color}` : 'none',
          borderRadius: line ? 0 : diamond ? 0 : 3,
          transform: diamond ? 'rotate(45deg)' : undefined,
        }}
      />
      {label}
    </span>
  );
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function lastNonNull(arr: Array<Date | null>): Date | null {
  let best: Date | null = null;
  for (const d of arr) {
    if (d !== null && (best === null || d > best)) best = d;
  }
  return best;
}
